import React from "react";
import Modal from "react-modal";

import { useAppDispatch } from "../../../app/hooks";
import { InvoiceSuggestion } from "../../../models";
import { approveSuggestion, rejectSuggestion } from "../invoiceSuggestionsSlice";

import "./ApproveOverageModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
    maxWidth: "460px",
    width: "90%",
    padding: "0",
  },
};

interface Props {
  // The suggestion being actioned; null = modal closed.
  suggestion: InvoiceSuggestion | null;
  onClose: () => void;
  // Called after a successful approve or reject so the caller can refetch.
  onActioned: () => void;
}

// Formats the overage total using whole-pound math to match backend invoice.
function calcTotal(dumped: number, allowed: number, feePerPound: number): string {
  const overage = Math.floor(dumped - allowed);
  if (overage <= 0) return "$0.00";
  return `$${(overage * feePerPound).toFixed(2)}`;
}

// Human label for the order's dumpster size ("10" → "10 yd", "clean_fill" →
// "Clean fill"); passthrough for anything unexpected.
function sizeLabel(size: string): string {
  if (size === "clean_fill") return "Clean fill";
  if (size === "10" || size === "15") return `${size} yd`;
  return size;
}

export function ApproveOverageModal({ suggestion, onClose, onActioned }: Props) {
  const dispatch = useAppDispatch();
  const [dumped, setDumped] = React.useState(0);
  const [allowed, setAllowed] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Prefill from the suggestion each time the modal opens.
  React.useEffect(() => {
    if (!suggestion) return;
    setDumped(suggestion.suggestedDumped);
    setAllowed(suggestion.suggestedAllowed);
    setError(null);
  }, [suggestion]);

  const approveDisabled = saving || dumped <= allowed;

  const handleApprove = async () => {
    if (!suggestion) return;
    setSaving(true);
    setError(null);
    try {
      await dispatch(
        approveSuggestion({
          id: suggestion.id,
          body: { accepted_dumped: dumped, accepted_allowed: allowed },
        })
      ).unwrap();
      onActioned();
      onClose();
    } catch {
      setError("Could not approve. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!suggestion) return;
    setSaving(true);
    setError(null);
    try {
      await dispatch(rejectSuggestion(suggestion.id)).unwrap();
      onActioned();
      onClose();
    } catch {
      setError("Could not reject. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const fee = suggestion?.feePerPound ?? 0;
  const total = suggestion ? calcTotal(dumped, allowed, fee) : "$0.00";

  return (
    <Modal
      isOpen={suggestion !== null}
      onRequestClose={onClose}
      style={modalStyles}
      contentLabel="Approve overage"
    >
      <div className="approve-overage-modal">
        <div className="approve-overage-header">
          <h3>Approve overage</h3>
          <button
            className="approve-overage-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="approve-overage-body">
          {suggestion && (
            <div className="approve-overage-order">
              <span className="order-name">{suggestion.orderName}</span>
              <span className="order-address">{suggestion.orderAddress}</span>
              {suggestion.orderDumpsterSize && (
                <span className="order-meta">
                  {sizeLabel(suggestion.orderDumpsterSize)} · Base $
                  {suggestion.orderBasePrice}
                </span>
              )}
            </div>
          )}
          <label className="overage-field">
            <span>Dumped weight (lb)</span>
            <input
              type="number"
              min="0"
              step="1"
              value={dumped}
              onChange={(e) => setDumped(Number(e.target.value))}
            />
          </label>
          <label className="overage-field">
            <span>Allowed weight (lb)</span>
            <input
              type="number"
              min="0"
              step="1"
              value={allowed}
              onChange={(e) => setAllowed(Number(e.target.value))}
            />
          </label>
          <div className="overage-total">
            Overage total: <strong>{total}</strong>
          </div>
          {error && <div className="overage-error">{error}</div>}
        </div>
        <div className="approve-overage-footer">
          <button
            className="btn btn-reject"
            onClick={handleReject}
            disabled={saving}
          >
            {saving ? "Saving…" : "Reject"}
          </button>
          <button
            className="btn btn-approve"
            onClick={handleApprove}
            disabled={approveDisabled}
          >
            {saving ? "Saving…" : "Approve"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
